/*
  Peter Augustus homepage | UGC reels
  13 Oct 2026 | Theme 138814587088

  For sections/pa-home-ugc.liquid:
  - Reels set to play on their own loop muted while at least half on screen
    and pause when they leave it, so only what people can see downloads
    (the videos are preload="none").
  - Tap reels wait behind the play button and play once with sound; starting
    one pauses any other reel playing with sound; tapping the video pauses it.
  - With reduced motion nothing plays on its own; every reel shows the button.
  - A reel with an Instagram link opens it on click (the play button sits
    above the link); a reel playing with sound pauses as the visitor leaves.
  - On desktop the row can be dragged with the mouse. Snap is off while
    dragging, and a drag never counts as a click on a play button.
  Re-runs on shopify:section:load and tidies up on shopify:section:unload.
*/
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const controllers = new WeakMap();

  function pauseOthers(except) {
    document.querySelectorAll('.pa-ugc__reel.is-playing[data-ugc-sound] video').forEach((video) => {
      if (video !== except) video.pause();
    });
  }

  function bindReel(reel, signal, observer) {
    const video = reel.querySelector('video');
    const button = reel.querySelector('[data-ugc-play]');
    if (!video) return;
    const tap = reel.hasAttribute('data-ugc-tap') || still;

    video.addEventListener('playing', () => reel.classList.add('is-playing'), { signal });
    video.addEventListener('pause', () => reel.classList.remove('is-playing'), { signal });
    video.addEventListener('ended', () => { video.currentTime = 0; reel.classList.remove('is-playing'); }, { signal });

    // Leaving for Instagram pauses a reel playing with sound, so it does not carry on in this tab
    const link = reel.querySelector('[data-ugc-link]');
    if (link) link.addEventListener('click', () => { if (!video.muted) video.pause(); }, { signal });

    if (!tap) {
      observer.observe(video);
      return;
    }

    const withSound = reel.hasAttribute('data-ugc-tap');
    if (withSound) reel.setAttribute('data-ugc-sound', '');
    button.addEventListener('click', () => {
      if (withSound) {
        pauseOthers(video);
        video.muted = false;
      }
      video.play().catch(() => {});
    }, { signal });
    video.addEventListener('click', () => { if (!video.paused) video.pause(); }, { signal });
  }

  function bindDrag(carousel, signal) {
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startLeft = carousel.scrollLeft;
    }, { signal });
    window.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        carousel.classList.add('is-dragging');
      }
      if (moved) carousel.scrollLeft = startLeft - dx;
    }, { signal });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      carousel.classList.remove('is-dragging');
    }, { signal });
    // A drag ends with a click on whatever is under the pointer; swallow it
    carousel.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, { capture: true, signal });
    carousel.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  function init(section) {
    if (controllers.has(section)) return;
    const controller = new AbortController();
    controllers.set(section, controller);
    const { signal } = controller;
    if (still) section.classList.add('pa-ugc--still');

    // Play at half visible or more. isIntersecting is true for any sliver, so read the ratio,
    // and watch 0 as well so a reel scrolled straight past half still gets a call.
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, intersectionRatio }) => {
        if (intersectionRatio >= 0.5) target.play().catch(() => {});
        else target.pause();
      });
    }, { threshold: [0, 0.5, 1] });
    signal.addEventListener('abort', () => observer.disconnect());

    section.querySelectorAll('[data-ugc-reel]').forEach((reel) => bindReel(reel, signal, observer));
    section.querySelectorAll('[data-ugc-carousel]').forEach((carousel) => bindDrag(carousel, signal));
  }

  const scan = (root) => root.querySelectorAll('.pa-ugc').forEach(init);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.pa-ugc').forEach((section) => {
      const controller = controllers.get(section);
      if (controller) controller.abort();
      controllers.delete(section);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
})();
